<template>
  <div
    ref="containerRef"
    class="audio-waveform"
    :class="{ 'audio-waveform--interactive': interactive }"
    :role="interactive ? 'slider' : 'progressbar'"
    aria-label="Audio progress"
    :aria-valuenow="Math.round(progressPercent)"
    :aria-valuemin="0"
    :aria-valuemax="100"
    :aria-valuetext="`${formattedCurrentTime} of ${formattedDuration}`"
    :tabindex="interactive ? 0 : undefined"
    @click="handleClick"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
    @keydown="handleSliderKeyDown"
  >
    <canvas ref="canvasRef" class="audio-waveform__canvas" />

    <!-- Progress overlay -->
    <div
      class="audio-waveform__progress"
      :style="{ width: `${progressPercent}%` }"
    />

    <!-- Chapter markers -->
    <template v-if="chapters && chapters.length > 0">
      <div
        v-for="(chapter, idx) in chapterPositions"
        :key="idx"
        class="audio-waveform__chapter-marker"
        :style="{ left: `${chapter.percent}%` }"
        :title="chapter.title"
      />
    </template>

    <!-- Buffered indicator -->
    <div
      v-if="bufferedAhead"
      :key="bufferedAhead.startPercent"
      class="audio-waveform__buffered"
      :style="{
        left: `${bufferedAhead.startPercent}%`,
        width: `${bufferedAhead.widthPercent}%`,
      }"
    />

    <!-- Seek preview -->
    <div
      v-if="isHovering && interactive"
      class="audio-waveform__seek-preview"
      :style="{ left: `${hoverPercent}%` }"
    >
      <span class="audio-waveform__seek-time">
        {{ hoverChapterLabel ? `${hoverChapterLabel} — ` : '' }}{{ hoverTime }}
      </span>
    </div>

    <!-- Current time indicator -->
    <div
      class="audio-waveform__indicator"
      :style="{ left: `${progressPercent}%` }"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import type { WaveformData, AudioChapter } from '../types'
import { bufferedAt, type TimeRange } from '../../types/base'
import { formatTime, clamp } from '../utils/audio-utils'

const props = withDefaults(
  defineProps<{
    waveformData?: WaveformData
    currentTime: number
    duration: number
    buffered?: TimeRange[]
    interactive?: boolean
    barWidth?: number
    barGap?: number
    barRadius?: number
    playedColor?: string
    unplayedColor?: string
    bufferedColor?: string
    chapters?: AudioChapter[]
    /** Player theme — used to invalidate cached canvas colors on switch */
    theme?: 'dark' | 'light' | 'colorful'
  }>(),
  {
    interactive: true,
    barWidth: 3,
    barGap: 2,
    barRadius: 2,
  }
)

const emit = defineEmits<{
  seek: [time: number]
  seekStart: []
  seekEnd: []
}>()

// Refs
const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
const canvasRef = useTemplateRef<HTMLCanvasElement>('canvasRef')

// State
const isHovering = ref(false)
const hoverPercent = ref(0)
const isDragging = ref(false)

// Resolved at draw-time (not via `computed`) so a live theme swap — which
// only mutates CSS variables on an ancestor element, not any reactive Vue
// state — picks up the new colours on the next redraw. Caching these as
// computeds froze them to mount-time values: the corporate /example/player
// demo mounts with `theme=colorful` (Vuetify base = light, on-surface ≈
// black) and later switches to `dark` via postMessage, which used to leave
// the waveform painted in black on the now-black player background.

// For dark/light themes we derive colours directly from the theme prop
// instead of reading `getComputedStyle(--v-theme-on-surface)`. The CSS-var
// path was racy in the live-swap scenario on the corporate demo page: even
// with `flush: 'post'` the inherited custom property occasionally still
// resolved to the previous theme's value (Vuetify's `--v-theme-on-surface`
// update on the document root and Vue's scoped class swap on `.audio-player`
// don't commit in lockstep). The values here mirror the SCSS overrides in
// AudioPlayer.vue (`&--dark`, `&--light`).

const SURFACE_BY_THEME = {
  dark: '255, 255, 255',
  light: '26, 26, 26',
} as const

function resolveSurfaceRgb(): string | null {
  if (props.theme === 'dark' || props.theme === 'light') {
    return SURFACE_BY_THEME[props.theme]
  }
  // 'colorful' (or unset) — defer to the inherited CSS variable.
  const el = containerRef.value
  if (!el) return null
  const onSurface = getComputedStyle(el).getPropertyValue('--v-theme-on-surface').trim()
  return onSurface || null
}

function resolveUnplayedColor(): string {
  if (props.unplayedColor) return props.unplayedColor
  const rgb = resolveSurfaceRgb()
  return rgb ? `rgba(${rgb}, 0.36)` : 'rgba(160, 160, 160, 0.25)'
}

function resolvePlayedColor(): string {
  if (props.playedColor) return props.playedColor
  const el = containerRef.value
  if (!el) return 'rgb(136, 74, 255)'
  const style = getComputedStyle(el)
  const accent = style.getPropertyValue('--audio-accent-color').trim()
  if (accent) return accent
  const primary = style.getPropertyValue('--v-theme-primary').trim()
  if (primary) return `rgb(${primary})`
  return 'rgb(136, 74, 255)'
}

function resolveChapterColor(): string {
  const rgb = resolveSurfaceRgb()
  return rgb ? `rgba(${rgb}, 0.3)` : 'rgba(160, 160, 160, 0.35)'
}

// Computed
const progressPercent = computed(() => {
  if (props.duration <= 0) return 0
  return clamp((props.currentTime / props.duration) * 100, 0, 100)
})

const formattedCurrentTime = computed(() => formatTime(props.currentTime))
const formattedDuration = computed(() => formatTime(props.duration))

const hoverTime = computed(() => {
  const time = (hoverPercent.value / 100) * props.duration
  return formatTime(time)
})

const hoverChapterLabel = computed(() => {
  if (!props.chapters || props.chapters.length === 0) return ''
  const time = (hoverPercent.value / 100) * props.duration
  for (let i = props.chapters.length - 1; i >= 0; i--) {
    if (time >= props.chapters[i].startTime) {
      return props.chapters[i].title
    }
  }
  return ''
})

/**
 * What is ready ahead of the playhead, as a position on the waveform — see [`bufferedAt`] for
 * why it is one range and not every range the media element holds.
 */
const bufferedAhead = computed(() => {
  if (props.duration <= 0) return undefined
  const range = bufferedAt(props.buffered ?? [], props.currentTime)
  if (!range) return undefined
  return {
    startPercent: (range.start / props.duration) * 100,
    widthPercent: ((range.end - range.start) / props.duration) * 100,
  }
})

const chapterPositions = computed(() => {
  if (!props.chapters || props.duration <= 0) return []
  return props.chapters
    .filter((ch) => ch.startTime > 0)
    .map((ch) => ({
      percent: (ch.startTime / props.duration) * 100,
      title: ch.title,
    }))
})

/**
 * Draws the waveform on the canvas
 */
function drawWaveform(): void {
  const canvas = canvasRef.value
  const container = containerRef.value
  if (!canvas || !container) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const dpr = window.devicePixelRatio || 1
  const rect = container.getBoundingClientRect()

  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  canvas.style.width = `${rect.width}px`
  canvas.style.height = `${rect.height}px`

  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, rect.width, rect.height)

  const peaks = props.waveformData?.peaks
  if (!peaks || peaks.length === 0) {
    drawPlaceholderBars(ctx, rect.width, rect.height)
    return
  }

  const barWidth = props.barWidth
  const barGap = props.barGap
  const totalBarWidth = barWidth + barGap
  const numBars = Math.floor(rect.width / totalBarWidth)
  const centerY = rect.height / 2
  const maxBarHeight = rect.height * 0.8

  const resampledPeaks = resamplePeaks(peaks, numBars)
  const progressRatio = props.duration > 0 ? props.currentTime / props.duration : 0

  const playedFill = resolvePlayedColor()
  const unplayedFill = resolveUnplayedColor()

  for (let i = 0; i < numBars; i++) {
    const x = i * totalBarWidth
    const peak = resampledPeaks[i] || 0
    const barHeight = Math.max(4, peak * maxBarHeight)
    const y = centerY - barHeight / 2

    const barProgress = i / numBars
    const isPlayed = barProgress <= progressRatio

    ctx.fillStyle = isPlayed ? playedFill : unplayedFill

    drawRoundedRect(ctx, x, y, barWidth, barHeight, props.barRadius)
    ctx.fill()
  }

  // Draw chapter marker lines on canvas
  if (props.chapters && props.chapters.length > 0 && props.duration > 0) {
    ctx.strokeStyle = resolveChapterColor()
    ctx.lineWidth = 1
    for (const ch of props.chapters) {
      if (ch.startTime > 0) {
        const x = (ch.startTime / props.duration) * rect.width
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, rect.height)
        ctx.stroke()
      }
    }
  }
}

function drawPlaceholderBars(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const barWidth = props.barWidth
  const barGap = props.barGap
  const totalBarWidth = barWidth + barGap
  const numBars = Math.floor(width / totalBarWidth)
  const centerY = height / 2
  const maxBarHeight = height * 0.4
  const minBarHeight = height * 0.1

  const progressRatio = props.duration > 0 ? props.currentTime / props.duration : 0
  const playedFill = resolvePlayedColor()
  const unplayedFill = resolveUnplayedColor()

  for (let i = 0; i < numBars; i++) {
    const x = i * totalBarWidth
    const seed = Math.sin(i * 0.5) * 0.5 + 0.5
    const barHeight = minBarHeight + seed * (maxBarHeight - minBarHeight)
    const y = centerY - barHeight / 2

    const barProgress = i / numBars
    const isPlayed = barProgress <= progressRatio

    ctx.fillStyle = isPlayed ? playedFill : unplayedFill

    drawRoundedRect(ctx, x, y, barWidth, barHeight, props.barRadius)
    ctx.fill()
  }
}

function resamplePeaks(peaks: number[], numBars: number): number[] {
  if (peaks.length === numBars) return peaks
  if (peaks.length === 0 || numBars === 0) return []

  const result: number[] = []
  const ratio = peaks.length / numBars

  for (let i = 0; i < numBars; i++) {
    const start = Math.floor(i * ratio)
    const end = Math.floor((i + 1) * ratio)

    let max = 0
    for (let j = start; j < end && j < peaks.length; j++) {
      if (peaks[j] > max) max = peaks[j]
    }
    result.push(max)
  }

  return result
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function getPercentFromEvent(event: MouseEvent | Touch): number {
  const container = containerRef.value
  if (!container) return 0

  const rect = container.getBoundingClientRect()
  const x = event.clientX - rect.left
  return clamp((x / rect.width) * 100, 0, 100)
}

function getTimeFromPercent(percent: number): number {
  return (percent / 100) * props.duration
}

// Keyboard navigation for ARIA slider
function handleSliderKeyDown(event: KeyboardEvent): void {
  if (!props.interactive) return

  const step = event.shiftKey ? 30 : 10

  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      event.preventDefault()
      emit('seek', Math.max(0, props.currentTime - step))
      break
    case 'ArrowRight':
    case 'ArrowUp':
      event.preventDefault()
      emit('seek', Math.min(props.duration, props.currentTime + step))
      break
    case 'Home':
      event.preventDefault()
      emit('seek', 0)
      break
    case 'End':
      event.preventDefault()
      emit('seek', props.duration)
      break
  }
}

// Event handlers
function handleClick(event: MouseEvent): void {
  if (!props.interactive) return

  const percent = getPercentFromEvent(event)
  const time = getTimeFromPercent(percent)
  emit('seek', time)
}

function handleMouseDown(event: MouseEvent): void {
  if (!props.interactive) return
  event.preventDefault()

  isDragging.value = true
  emit('seekStart')

  const handleMouseMove = (e: MouseEvent) => {
    hoverPercent.value = getPercentFromEvent(e)
    const time = getTimeFromPercent(hoverPercent.value)
    emit('seek', time)
  }

  const handleMouseUp = () => {
    isDragging.value = false
    emit('seekEnd')
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function handleTouchStart(event: TouchEvent): void {
  if (!props.interactive) return
  event.preventDefault()

  isDragging.value = true
  emit('seekStart')

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      hoverPercent.value = getPercentFromEvent(e.touches[0])
      const time = getTimeFromPercent(hoverPercent.value)
      emit('seek', time)
    }
  }

  const handleTouchEnd = () => {
    isDragging.value = false
    emit('seekEnd')
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }

  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)
}

function handleMouseEnter(): void {
  isHovering.value = true
}

function handleMouseLeave(): void {
  if (!isDragging.value) {
    isHovering.value = false
  }
}

function handleMouseMove(event: MouseEvent): void {
  if (isHovering.value || isDragging.value) {
    hoverPercent.value = getPercentFromEvent(event)
  }
}

// Resize observer
useResizeObserver(containerRef, () => {
  drawWaveform()
})

// Watch for data changes and color changes. `flush: 'post'` waits for Vue to
// commit the DOM/class updates first — without it, a theme swap would read
// stale CSS variables on the same tick the new class is being applied.
watch(
  () => [props.waveformData, props.currentTime, props.duration, props.chapters, props.playedColor, props.unplayedColor, props.theme],
  () => {
    drawWaveform()
  },
  { deep: true, flush: 'post' }
)

// Lifecycle
onMounted(() => {
  const container = containerRef.value
  if (container) {
    container.addEventListener('mouseenter', handleMouseEnter)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('mousemove', handleMouseMove)
  }
  drawWaveform()
})

onUnmounted(() => {
  const container = containerRef.value
  if (container) {
    container.removeEventListener('mouseenter', handleMouseEnter)
    container.removeEventListener('mouseleave', handleMouseLeave)
    container.removeEventListener('mousemove', handleMouseMove)
  }
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-waveform {
  position: relative;
  width: 100%;
  height: 72px;
  user-select: none;

  @include focus-visible-only;

  &--interactive {
    cursor: pointer;
  }

  &__canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  &__progress {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    pointer-events: none;
  }

  &__chapter-marker {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    pointer-events: none;
  }

  &__buffered {
    position: absolute;
    top: 50%;
    height: 2px;
    transform: translateY(-50%);
    background: rgba(var(--v-theme-on-surface), 0.1);
    pointer-events: none;
  }

  &__indicator {
    position: absolute;
    top: 50%;
    width: 3px;
    height: 80%;
    transform: translate(-50%, -50%);
    background: var(--audio-accent-color, rgb(var(--v-theme-primary)));
    border-radius: 2px;
    pointer-events: none;
    box-shadow: 0 0 8px rgba(var(--audio-accent-rgb, var(--v-theme-primary)), 0.4);
  }

  &__seek-preview {
    position: absolute;
    top: -32px;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &__seek-time {
    display: block;
    padding: $space-xs $space-sm;
    font-size: $text-sm;
    font-weight: $font-medium;
    font-variant-numeric: tabular-nums;
    color: white;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    border-radius: $radius-sm;
    white-space: nowrap;
  }
}
</style>
